import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Monitor, Cloud, Layers, Laptop, Globe, CheckCircle } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";

export default function DigitalisierungPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  usePageSeo({
    title: "Digitalisierung für KMU | LAB336 – Digitale Transformation Schweiz",
    description: "Digitale Transformation für Schweizer KMU. Von der Digitalstrategie über Cloud-Migration bis zur System-Integration – wir begleiten Ihren Weg in die digitale Zukunft.",
    canonical: `${BASE_URL}/digitalisierung`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "Digitalisierung",
          "description": "Entwicklung und Umsetzung massgeschneiderter Digitalstrategien für KMU",
          "provider": { "@type": "Organization", "name": "LAB336", "url": BASE_URL },
          "areaServed": { "@type": "Country", "name": "Schweiz" },
          "serviceType": "Digitalisierungsberatung"
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
            { "@type": "ListItem", "position": 2, "name": "Leistungen", "item": `${BASE_URL}/#services` },
            { "@type": "ListItem", "position": 3, "name": "Digitalisierung", "item": `${BASE_URL}/digitalisierung` }
          ]
        }
      ]
    }
  });

  const goToScheduler = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#scheduler");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="service-page-digitalisierung">
      <header className="bg-lab-primary text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl tracking-wide">LAB<span className="text-lab-accent">336</span></Link>
          <Link to="/" className="text-sm text-white/80 hover:text-white flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Startseite
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 bg-white flex items-center justify-center mb-6">
              <Monitor className="w-8 h-8 text-lab-accent" strokeWidth={1.5} />
            </div>
            <span className="text-lab-accent text-sm font-semibold tracking-widest uppercase">Leistung</span>
            <h1 className="font-playfair text-3xl lg:text-5xl font-semibold text-lab-primary mt-4 max-w-3xl">
              Digitale Transformation für Schweizer KMU
            </h1>
            <p className="text-lab-text-muted text-lg mt-6 max-w-2xl leading-relaxed">
              Ihr Unternehmen verdient mehr als Excel-Listen und Insellösungen. Wir begleiten Sie Schritt für Schritt in die digitale Zukunft — pragmatisch und auf Ihr Tempo abgestimmt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nutzenargumentation */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-6">Warum Digitalisierung kein Luxus mehr ist</h2>
            <div className="text-lab-text-muted leading-relaxed space-y-4">
              <p>
                Viele Schweizer KMU arbeiten noch mit Systemen und Abläufen, die vor zehn oder fünfzehn Jahren eingeführt wurden. Was damals funktioniert hat, bremst heute: Daten liegen in verschiedenen Programmen verstreut, Abteilungen arbeiten aneinander vorbei, und wichtige Entscheidungen basieren auf veralteten Informationen. Das Tagesgeschäft läuft irgendwie — aber effizienter, schneller und wettbewerbsfähiger geht es definitiv.
              </p>
              <p>
                Der grösste Irrtum bei der Digitalisierung: dass es um Technologie geht. In Wahrheit geht es um Ihre Arbeitsweise. Welche Informationen braucht Ihr Team, um gute Entscheidungen zu treffen? Wo verbringen Mitarbeitende Zeit mit Aufgaben, die ein System schneller erledigen könnte? Welche Abläufe funktionieren nur, weil eine bestimmte Person sich darum kümmert — und was passiert, wenn diese Person ausfällt?
              </p>
              <p>
                Wir beantworten diese Fragen gemeinsam mit Ihnen und Ihrem Team. Dabei geht es nicht darum, alles auf einmal umzukrempeln. Wir identifizieren die Bereiche mit dem grössten Hebel, setzen dort zuerst an und bauen schrittweise darauf auf. So sehen Sie schnell erste Ergebnisse, ohne dass Ihr Tagesgeschäft darunter leidet.
              </p>
              <p>
                Das Ziel: Ein Unternehmen, in dem Informationen fliessen, Prozesse ineinandergreifen und Ihr Team die Werkzeuge hat, die es braucht — nicht mehr und nicht weniger.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4">So sieht Digitalisierung in der Praxis aus</h2>
            <p className="text-lab-text-muted mb-10">Beispiele aus dem Schweizer KMU-Alltag:</p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Handwerksbetrieb mit 15 Mitarbeitenden",
                  problem: "Aufträge wurden auf Papier erfasst, Rechnungen manuell geschrieben. Monatsabschluss dauerte eine Woche.",
                  solution: "Digitales Auftragsmanagement mit automatischer Rechnungsstellung und Buchhaltungsanbindung.",
                  result: "Monatsabschluss in einem Tag, 60% weniger Administrations-aufwand."
                },
                {
                  title: "Immobilienverwaltung mit 20 Mitarbeitenden",
                  problem: "Mieteranfragen kamen per Telefon, E-Mail und Post — ohne zentrale Erfassung. Anfragen gingen regelmässig verloren.",
                  solution: "Zentrales Ticketsystem mit Mieterportal und automatischen Statusupdates.",
                  result: "Keine verlorenen Anfragen mehr, Bearbeitungszeit -50%."
                },
                {
                  title: "Gastrobetrieb mit 3 Standorten",
                  problem: "Jeder Standort nutzte eigene Systeme. Zentrale Auswertung und Bestellplanung war unmöglich.",
                  solution: "Cloud-basiertes POS-System mit zentralem Dashboard und automatischer Bestelloptimierung.",
                  result: "Echtzeit-Überblick über alle Standorte, Wareneinsatz -18%."
                },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-lab-border p-6">
                  <h3 className="font-playfair text-lg font-semibold text-lab-primary mb-3">{item.title}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Problem:</span> {item.problem}</p>
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Lösung:</span> {item.solution}</p>
                    <p className="text-lab-accent font-medium"><CheckCircle className="w-4 h-4 inline mr-1" />{item.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-8">Unsere Digitalisierungsleistungen im Detail</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Globe, title: "Digitalstrategie", text: "Wir entwickeln eine klare Roadmap für Ihre digitale Transformation — abgestimmt auf Ihre Ziele, Ressourcen und Branche. Kein Buzzword-Bingo, sondern ein konkreter Plan mit messbaren Meilensteinen." },
              { icon: Layers, title: "System-Integration", text: "Bestehende Systeme nahtlos verbinden: ERP, CRM, Buchhaltung und weitere Tools zu einem effizienten Gesamtsystem verknüpfen. Schluss mit doppelter Datenpflege und Informationssilos." },
              { icon: Cloud, title: "Cloud Migration", text: "Sichere und schrittweise Migration Ihrer Infrastruktur in die Cloud — für Flexibilität, Skalierbarkeit und Kostentransparenz. Ohne Risiko für Ihren laufenden Betrieb." },
              { icon: Laptop, title: "Digital Workplace", text: "Moderne Arbeitsumgebungen für Ihr Team: Kollaborationstools, mobile Lösungen und digitale Workflows. Ihre Mitarbeitenden arbeiten effizienter — im Büro und unterwegs." },
            ].map((item) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                className="bg-lab-bg-light p-8 border border-lab-border">
                <item.icon className="w-6 h-6 text-lab-accent mb-4" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl font-semibold text-lab-primary mb-3">{item.title}</h3>
                <p className="text-lab-text-muted leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorgehen */}
      <section className="bg-lab-bg-dark py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-12">Unser Vorgehen in 4 Schritten</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Assessment", text: "Digitaler Reifegrad evaluieren, Schmerzpunkte identifizieren und Potenziale priorisieren — gemeinsam mit Ihrem Team." },
              { step: "02", title: "Strategie", text: "Massgeschneiderte Digitalstrategie mit klaren Meilensteinen, realistischem Budget und Quick Wins." },
              { step: "03", title: "Umsetzung", text: "Schrittweise Implementierung mit agilen Methoden. Regelmässige Demos und Feedback-Schleifen." },
              { step: "04", title: "Optimierung", text: "Laufende Verbesserung und Anpassung an neue Anforderungen. Ihr Team wird befähigt, eigenständig weiterzuarbeiten." },
            ].map((item, i) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}
                className="text-white">
                <span className="text-lab-accent text-4xl font-playfair font-semibold">{item.step}</span>
                <h3 className="font-playfair text-xl font-semibold mt-4 mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-lab-primary py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-4">
            Ihr Unternehmen ist bereit — sind Sie es auch?
          </h2>
          <p className="text-gray-300 mb-4 leading-relaxed">
            In einem kostenlosen 30-Minuten-Gespräch bewerten wir gemeinsam Ihren digitalen Reifegrad und zeigen Ihnen, wo die grössten Hebel für Ihr Unternehmen liegen.
          </p>
          <p className="text-lab-accent font-medium mb-8">
            Keine Theorie-Präsentation. Sondern konkrete, umsetzbare Empfehlungen für Ihre Situation.
          </p>
          <button onClick={goToScheduler} className="bg-lab-accent text-lab-primary font-semibold px-10 py-4 text-base inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="cta-scheduler">
            Kostenlosen Digital-Check buchen <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
}
